import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {SocialMediaProfileLinksProps} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {SocialMediaProfile, SocialMediaProfileDisplayName} from '@Components/social-media-profile-links/social-media-profile-links.types';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {getSocialIconPath, initSocialMediaLinksErrorStates, initSocialMediaLinksVisibilityStates} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {InputFieldState} from '@Components/input-field-v2/input-field.types';
import {useAllSocialMediaAccountUsernames} from '@Components/social-media-profile-links/social-media-profile-links.hooks';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import type {SuggestionListItemProps} from '@Components/input-suggestions-dropdown/suggestion-list-item/suggestion-list-item.types';
import styles from './social-media-profile-links.module.scss';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getReadBucket} from '@Utils/s3.util';
import {repoURL} from '@Libraries/s3-library';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';

export function SocialMediaProfileLinks({
  socialMediaProfileLinks,
  onInputChangeCallback,
  errorStates = initSocialMediaLinksErrorStates(),
  onInputBlurCallback,
  containerClassName = 'spacing-m-t-10',
  classMoreOptionsDropdown = '',
}: SocialMediaProfileLinksProps): ReactElement<any> {
  const [socialInputsVisibilityMap, setSocialInputsVisibilityMap] = useState(initSocialMediaLinksVisibilityStates(socialMediaProfileLinks));
  const allSocialMediaAccountUsernames = useAllSocialMediaAccountUsernames();
  const [isMoreAccountsButtonActive, setIsMoreAccountButtonActive] = useState(false);

  const onInputChange = (socialMediaProfileName: SocialMediaProfile, url: string): void => {
    onInputChangeCallback(socialMediaProfileName, url);
  };
  const onInputBlur = (socialMediaProfileName: SocialMediaProfile): void => {
    if (onInputBlurCallback) {
      onInputBlurCallback(socialMediaProfileName);
    }
  };

  const getPrimaryDropdownSuggestions = (socialMediaPlatform: SocialMediaProfile, icon: string): SuggestionListItemProps[] => {
    const socialMediaUsernames = allSocialMediaAccountUsernames[socialMediaPlatform];
    const suggestionItems: SuggestionListItemProps[] = [];
    socialMediaUsernames.forEach((item) => {
      suggestionItems.push({
        primaryText: item,
        primaryIcon: icon,
        secondaryIcon: 'icon-social-media',
        secondaryText: window.i18next.t('pmwjs_social_posts'),
      });
    });

    return suggestionItems;
  };

  const getImageIconUri = (socialIconName: string): string => {
    return `assets/social-icons/original/${socialIconName}.png`;
  };

  const getInstagramInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.INSTAGRAM]}
        placeholder="www.instagram.com/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.INSTAGRAM, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.INSTAGRAM);
        }}
        hasIcon="icon-instagram"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.INSTAGRAM] ? getImageIconUri('social-icon-instagram') : ''}
        className="spacing-m-t-1"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.INSTAGRAM] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.INSTAGRAM] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.INSTAGRAM]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.INSTAGRAM,
              })
            : ''
        }
      />
    );
  };

  const getFacebookInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.FACEBOOK]}
        placeholder="www.facebook.com/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.FACEBOOK, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.FACEBOOK);
        }}
        hasIcon="icon-facebook"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.FACEBOOK] ? getImageIconUri('social-icon-facebook') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.FACEBOOK] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.FACEBOOK] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.FACEBOOK]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.FACEBOOK,
              })
            : ''
        }
      />
    );
  };

  const getXInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.X]}
        placeholder="www.x.com/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.X, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.X);
        }}
        hasIcon="icon-x-twitter"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.X] ? getImageIconUri('social-icon-x') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.X] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.X] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.X]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.X,
              })
            : ''
        }
      />
    );
  };

  const getYouTubeInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.YOUTUBE]}
        placeholder="www.youtube.com/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.YOUTUBE, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.YOUTUBE);
        }}
        hasIcon="icon-youtube"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.YOUTUBE] ? getImageIconUri('social-icon-youtube') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.YOUTUBE] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.YOUTUBE] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.YOUTUBE]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.YOUTUBE,
              })
            : ''
        }
      />
    );
  };

  const getTiktokInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.TIKTOK]}
        placeholder="www.tiktok.com/@username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.TIKTOK, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.TIKTOK);
        }}
        hasIcon="icon-tiktok"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.TIKTOK] ? getImageIconUri('social-icon-tiktok') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.TIKTOK] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.TIKTOK] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.TIKTOK]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.TIKTOK,
              })
            : ''
        }
      />
    );
  };

  const getPinterestInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.PINTEREST]}
        placeholder="www.pinterest.com/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.PINTEREST, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.PINTEREST);
        }}
        hasIcon="icon-pinterest"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.PINTEREST] ? getImageIconUri('social-icon-pinterest') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.PINTEREST] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.PINTEREST] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.PINTEREST]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.PINTEREST,
              })
            : ''
        }
      />
    );
  };

  const getLinkedInInput = (): ReactElement<any> => {
    return (
      <InputField
        input={socialMediaProfileLinks[SocialMediaProfile.LINKEDIN]}
        placeholder="www.linkedin.com/company/username"
        onInputChange={(value: string) => {
          onInputChange(SocialMediaProfile.LINKEDIN, value);
        }}
        onInputBlur={() => {
          onInputBlur(SocialMediaProfile.LINKEDIN);
        }}
        hasIcon="icon-linekdin"
        hasImageIcon={socialMediaProfileLinks[SocialMediaProfile.LINKEDIN] ? getImageIconUri('social-icon-linkedin') : ''}
        className="spacing-m-t-2"
        iconClassName={socialMediaProfileLinks[SocialMediaProfile.LINKEDIN] ? styles.activeIconColor : 'content-disabled'}
        state={errorStates[SocialMediaProfile.LINKEDIN] ? InputFieldState.ERROR : InputFieldState.DEFAULT}
        information={
          errorStates[SocialMediaProfile.LINKEDIN]
            ? window.i18next.t('pmwjs_x_social_media_link_is_invalid', {
                socialPlatform: SocialMediaProfileDisplayName.LINKEDIN,
              })
            : ''
        }
      />
    );
  };

  const getMoreAccountsCta = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        size={Size.XSMALL}
        icon="icon-plus"
        text={window.i18next.t('pmwjs_more_accounts')}
        customClasses="spacing-m-t-2"
        isActive={isMoreAccountsButtonActive}
      />
    );
  };

  const onClickSocialProfileOption = (socialMediaProfile: SocialMediaProfile): void => {
    setSocialInputsVisibilityMap((prevStates) => ({
      ...prevStates,
      [socialMediaProfile]: true,
    }));
  };

  const shouldShowMoreAccountsCta = (): boolean => {
    return !Object.values(socialInputsVisibilityMap).every((value) => value);
  };

  const getMoreAccountsDropdown = (): ReactElement<any> => {
    return (
      <Dropdown
        selector={getMoreAccountsCta()}
        popup={getPopupContent()}
        className="flex-vertical-start"
        popupClassName={`spacing-p-3 border-s-standard ${styles.socialsList} ${classMoreOptionsDropdown}`}
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_accounts')}
        modalClassName="spacing-p-3"
        popupOffset={8}
        onOpen={() => {
          setIsMoreAccountButtonActive(true);
        }}
        onClose={() => {
          setIsMoreAccountButtonActive(false);
        }}
      />
    );
  };

  const isLastPopupItem = (socialMediaProfile: SocialMediaProfile): boolean => {
    const keys = Object.keys(socialInputsVisibilityMap).filter((socialProfileName: string): boolean => {
      return !socialInputsVisibilityMap[socialProfileName as SocialMediaProfile];
    });
    return !socialInputsVisibilityMap[socialMediaProfile] && keys.indexOf(socialMediaProfile) === keys.length - 1;
  };

  const getPopupListItem = (socialMediaProfile: SocialMediaProfile, profileDisplayName: SocialMediaProfileDisplayName, imageSrc: string): ReactElement<any> | null => {
    return !socialInputsVisibilityMap[socialMediaProfile] ? (
      <ControlledListItemType3
        id={`share-to-${socialMediaProfile}`}
        text={profileDisplayName}
        onClick={() => {
          onClickSocialProfileOption(socialMediaProfile);
        }}
        thumbnailClassName={`${styles.thumbnail} _borderless`}
        thumbnail={{
          type: ThumbnailType.IMAGE,
          imageSrc: imageSrc,
        }}
        isItemUnmargined={isLastPopupItem(socialMediaProfile)}
      />
    ) : null;
  };

  const getPopupContent = (): ReactElement<any> => {
    return (
      <ul className="_unmargin _full-width flex-v-row">
        {getPopupListItem(SocialMediaProfile.X, SocialMediaProfileDisplayName.X, repoURL('assets/social-icons/circle-black/social-icon-x.png', getReadBucket()))}
        {getPopupListItem(SocialMediaProfile.YOUTUBE, SocialMediaProfileDisplayName.YOUTUBE, getSocialIconPath('social-icon-youtube'))}
        {getPopupListItem(SocialMediaProfile.TIKTOK, SocialMediaProfileDisplayName.TIKTOK, getSocialIconPath('social-icon-tiktok'))}
        {getPopupListItem(SocialMediaProfile.PINTEREST, SocialMediaProfileDisplayName.PINTEREST, getSocialIconPath('social-icon-pinterest'))}
        {getPopupListItem(SocialMediaProfile.LINKEDIN, SocialMediaProfileDisplayName.LINKEDIN, getSocialIconPath('social-icon-linkedin'))}
      </ul>
    );
  };

  const getVisibilityClassForSocialPlatform = (socialProfile: SocialMediaProfile): string => {
    return socialMediaProfileLinks[socialProfile] !== '' || socialInputsVisibilityMap[socialProfile] ? '' : '_hidden';
  };

  return (
    <div className={`${containerClassName} _full-width flex-v-row `}>
      <Text val={window.i18next.t('pmwjs_social_media_profiles')} size={TextSize.XXSMALL} bold className="spacing-m-b-1 content-body" />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.INSTAGRAM}
        inputSelector={getInstagramInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.INSTAGRAM, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.INSTAGRAM, 'icon-instagram')}
        className={socialInputsVisibilityMap[SocialMediaProfile.INSTAGRAM] ? '' : '_hidden'}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.FACEBOOK}
        inputSelector={getFacebookInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.FACEBOOK, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.FACEBOOK, 'icon-facebook')}
        className={socialInputsVisibilityMap[SocialMediaProfile.FACEBOOK] ? '' : '_hidden'}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.X}
        inputSelector={getXInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.X, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.X, 'icon-x-twitter')}
        className={getVisibilityClassForSocialPlatform(SocialMediaProfile.X)}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.YOUTUBE}
        inputSelector={getYouTubeInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.YOUTUBE, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.YOUTUBE, 'icon-youtube')}
        className={getVisibilityClassForSocialPlatform(SocialMediaProfile.YOUTUBE)}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.TIKTOK}
        inputSelector={getTiktokInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.TIKTOK, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.TIKTOK, 'icon-tiktok')}
        className={getVisibilityClassForSocialPlatform(SocialMediaProfile.TIKTOK)}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.PINTEREST}
        inputSelector={getPinterestInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.PINTEREST, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.PINTEREST, 'icon-pinterest')}
        className={getVisibilityClassForSocialPlatform(SocialMediaProfile.PINTEREST)}
      />
      <InputSuggestionsDropdown
        dropdownId={SocialMediaProfile.LINKEDIN}
        inputSelector={getLinkedInInput()}
        onSuggestionSelect={(value: string) => {
          onInputChange(SocialMediaProfile.LINKEDIN, value);
        }}
        primarySuggestionsList={getPrimaryDropdownSuggestions(SocialMediaProfile.LINKEDIN, 'icon-linekdin')}
        className={getVisibilityClassForSocialPlatform(SocialMediaProfile.LINKEDIN)}
      />
      {shouldShowMoreAccountsCta() ? getMoreAccountsDropdown() : null}
    </div>
  );
}
